import axiosClient from '@/common/AxiosClient';
import { COURSE_RECURRENCE, CREATE_TRAINING_MODE } from '@/constants';
import { queryKeys } from '@/hooks/queryKeys';
import { useAppMutation } from '@/hooks/useAppMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { CreateTrainingFormValues } from '@/interfaces/Training';
import { useLocale } from '@/providers/LocaleProvider';
import { showGlobalSnackbar } from '@/providers/SnackbarProvider';
import { AttributeDefinitionApiData } from '@/types';
import { BaseSelectorOption } from '@/types/components/selector.types';
import { CreateTrainingSchema } from '@/utilities/validationSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Alert, ConfirmationDialog } from 'klayowebshared';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SelectMenu } from '../SelectMenu';
import { MultiAttributeSelector } from '../selectors/MultiAttributeSelector';
import { TrainingSelector } from '../selectors/TrainingSelector';
import { TextField } from '../TextField';
import { isValidTrainingImage, TrainingImageField } from '../TrainingImageField';
import { Dialog } from './Dialog';

const defaultValues: CreateTrainingFormValues = {
  trainingName: '',
  requiredFaculties: '',
  recurrence: null,
  selectedCompetencies: [],
  selectedIntegrationId: undefined,
};

export interface AddTrainingBeforeSaveDetail {
  name: string;
  associatedAttribute: any[];
  recurrence: number;
}

interface AddTrainingDialogProps {
  open: boolean;
  onClose: () => void;
  theme?: any;
  mode?: number;
  trainingData?: any;
  onBeforeSave: (detail: AddTrainingBeforeSaveDetail) => boolean;
  manualIntegrations: BaseSelectorOption[];
}

export const AddTrainingDialog = ({
  open,
  onClose,
  theme,
  mode = CREATE_TRAINING_MODE.CREATE,
  trainingData,
  onBeforeSave,
  manualIntegrations,
}: AddTrainingDialogProps) => {
  const { translate } = useLocale();
  const queryClient = useQueryClient();
  const isInitialized = useRef(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [imageInvalid, setImageInvalid] = useState(false);
  const [isSavingImage, setIsSavingImage] = useState(false);

  const isEditMode = mode === CREATE_TRAINING_MODE.EDIT;

  const { data: attributesData, isLoading: isAttributeLoading } = useAppQuery<{
    attributeDefinitions: AttributeDefinitionApiData[];
  }>('/AttributeDefinition', {
    config: {
      params: {
        includeParentAttributes: true,
      },
    },
  });

  const {
    mutateAsync: createCourse,
    isPending: isCreating,
    error: createError,
  } = useAppMutation<AxiosResponse<{ integrationCourse: { id: string } }>, Error, any>(
    'post',
    '/Integration/Course/Add'
  );

  const {
    mutateAsync: updateCourse,
    isPending: isUpdating,
    error: updateError,
  } = useAppMutation<unknown, Error, any>('put', '/Integration/Course/Update');

  const isSaving = isCreating || isUpdating || isSavingImage;
  const error = createError || updateError;

  const [filePreviewUrl, setFilePreviewUrl] = useState<string>();
  useEffect(() => {
    if (!imageFile) {
      setFilePreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);
  const previewUrl =
    filePreviewUrl ?? (isEditMode && !imageRemoved ? trainingData?.thumbnailUrl : undefined);

  const attributes = useMemo(() => attributesData?.attributeDefinitions ?? [], [attributesData]);

  const recurrenceItems = useMemo(
    () =>
      Object.entries(COURSE_RECURRENCE) as [
        string,
        (typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE],
      ][],
    []
  );

  const getRecurrenceLabel = useCallback(
    (item: (typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE]) => translate(item.label),
    [translate]
  );

  const findRecurrenceItem = useCallback(
    (value: number) => recurrenceItems.find(([, item]) => item.value === value)?.[1] ?? null,
    [recurrenceItems]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CreateTrainingFormValues>({
    resolver: zodResolver(CreateTrainingSchema),
    defaultValues,
  });

  const isChangedForm = !isEmpty(dirtyFields) || imageFile !== null || imageRemoved;

  const resetImage = () => {
    setImageFile(null);
    setImageRemoved(false);
    setImageInvalid(false);
  };

  const onSelectImage = (file: File) => {
    if (!isValidTrainingImage(file)) {
      setImageInvalid(true);
      return;
    }
    setImageInvalid(false);
    setImageFile(file);
    setImageRemoved(false);
  };

  const onRemoveImage = () => {
    setImageInvalid(false);
    setImageFile(null);
    // Only a saved image needs deleting; dropping a file picked in this session changes nothing.
    setImageRemoved(isEditMode && !!trainingData?.thumbnailUrl);
  };

  // A separate request after the course is saved, because a new course needs its id first.
  const saveImage = async (courseId: string) => {
    if (imageFile) {
      const formData = new FormData();
      formData.append('file', imageFile);
      await axiosClient.post(`/Integration/Course/${courseId}/Image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else if (imageRemoved && isEditMode) {
      await axiosClient.delete(`/Integration/Course/${courseId}/Image`);
    }
  };

  useEffect(() => {
    if (open) {
      isInitialized.current = false;
      resetImage();
      if (!isEditMode) {
        reset(defaultValues);
      }
    }
  }, [open, isEditMode, reset]);

  useEffect(() => {
    if (isEditMode && trainingData && !isInitialized.current) {
      reset({
        trainingName: trainingData.courseName || '',
        requiredFaculties: trainingData.facultiesAsString || '',
        recurrence: findRecurrenceItem(trainingData.recurrence),
        selectedCompetencies: trainingData.competencies || [],
        selectedIntegrationId: trainingData.integrationId
          ? {
              id: trainingData.integrationId,
              name: trainingData.integrationName || '',
            }
          : undefined,
      });
      isInitialized.current = true;
    }
  }, [isEditMode, trainingData, reset, findRecurrenceItem]);

  const onSubmit = async (data: CreateTrainingFormValues) => {
    const recurrenceValue = data.recurrence?.value ?? 0;

    const canProceed = onBeforeSave({
      name: data.trainingName,
      associatedAttribute: data.selectedCompetencies,
      recurrence: recurrenceValue,
    });

    if (!canProceed) return;

    const payload = {
      organizationIntegrationId: data.selectedIntegrationId?.id,
      courseName: data.trainingName,
      faculties: data.requiredFaculties,
      recurrence: recurrenceValue,
      competencies: data.selectedCompetencies.map((c: any) => c.attributeDefinitionId),
    };

    let courseId: string | undefined;
    try {
      if (isEditMode) {
        await updateCourse({ courseId: trainingData?.id, ...payload });
        courseId = trainingData?.id;
      } else {
        const response = await createCourse(payload);
        courseId = response.data?.integrationCourse?.id;
      }
    } catch {
      return; // shown in the dialog's error alert
    }

    setIsSavingImage(true);
    try {
      if (courseId) await saveImage(courseId);
      else if (imageFile) showGlobalSnackbar(translate('dialogs.trainingImageNotSaved'), 'error');
    } catch {
      // The course itself is saved; the request's error snackbar reports the image failure.
    } finally {
      setIsSavingImage(false);
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.trainingCourses.list() });
    onCloseDialog();
  };

  const onShowWarnCloseDialog = () => {
    // Closing mid-save would let the pending save close (and reset) whichever dialog is open next.
    if (isSaving) return;
    if (isChangedForm) {
      setShowConfirmClose(true);
    } else {
      onCloseDialog();
    }
  };

  const onCloseDialog = () => {
    reset(defaultValues);
    resetImage();
    onClose();
  };

  return (
    <Dialog
      open={open}
      theme={theme}
      onClose={onShowWarnCloseDialog}
      fullWidth
      maxWidth='sm'
      title={isEditMode ? translate('dialogs.editTraining') : translate('dialogs.createTraining')}
      actions={[
        {
          label: isEditMode ? translate('common.save') : translate('common.create'),
          primary: true,
          variant: 'filled',
          disabled: !isChangedForm || isSaving,
          onClick: handleSubmit(onSubmit),
          isLoading: isSaving,
        },
        {
          label: translate('common.cancel'),
          onClick: onShowWarnCloseDialog,
          disabled: isSaving,
        },
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction='column' spacing={4}>
          {error && (
            <Alert severity='error' sx={{ width: '100%' }}>
              {error.message}
            </Alert>
          )}

          <Controller
            name='selectedIntegrationId'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TrainingSelector
                label={translate('dialogs.trainingProviderRequired')}
                options={manualIntegrations as any}
                onChange={(_, newValue) => field.onChange(newValue)}
                placeholder={translate('dialogs.pleaseSelectOption')}
                value={field.value ?? null}
                errorMessage={error?.message}
                error={!!error?.message}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                translate={translate}
                disabled={isEditMode}
              />
            )}
          />

          <Controller
            name='trainingName'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                value={field.value}
                label={translate('dialogs.nameOfTrainingRequired')}
                dense
                autoComplete={false}
                sx={{ width: '100%' }}
                onChange={(e: any) => field.onChange(e.target.value)}
                error={!!error?.message}
                helperText={error?.message ? translate(error.message) : undefined}
              />
            )}
          />

          <TrainingImageField
            previewUrl={previewUrl}
            error={imageInvalid}
            disabled={isSaving}
            onSelect={onSelectImage}
            onRemove={onRemoveImage}
          />

          <Controller
            name='requiredFaculties'
            control={control}
            render={({ field }) => (
              <TextField
                value={field.value}
                label={translate('dialogs.facultiesOptional')}
                dense
                autoComplete={false}
                sx={{ width: '100%' }}
                onChange={(e: any) => field.onChange(e.target.value)}
              />
            )}
          />

          <Controller
            name='recurrence'
            control={control}
            render={({ field }) => (
              <SelectMenu<(typeof COURSE_RECURRENCE)[keyof typeof COURSE_RECURRENCE]>
                label={translate('dialogs.recurrenceOptional')}
                value={field.value != null ? findRecurrenceItem(field.value.value) : null}
                firstValueDefault
                dense
                items={recurrenceItems}
                getItemLabel={getRecurrenceLabel}
                onChange={(e) => field.onChange(e.target.value)}
                sx={{
                  width: '100%',
                  '& fieldset legend span': {
                    display: 'none',
                  },
                }}
              />
            )}
          />

          <Controller
            name='selectedCompetencies'
            control={control}
            render={({ field }) => (
              <MultiAttributeSelector
                title={translate('entities.attribute')}
                placeHolder={translate('dialogs.searchAndAddCompetencies')}
                multiple
                values={field.value}
                items={attributes.filter(
                  (a) =>
                    !field.value.some(
                      (v: any) => v.attributeDefinitionId === a.attributeDefinitionId
                    )
                )}
                getOptionLabel={(option) => (option as any).name || ''}
                onChange={(_event: any, newValue: any) => {
                  field.onChange(newValue);
                }}
                hasNewItem={false}
                translate={translate}
                isLoading={isAttributeLoading}
              />
            )}
          />
        </Stack>
      </form>

      {showConfirmClose && (
        <ConfirmationDialog
          title={translate('dialogs.confirmCancel')}
          theme={theme}
          question={
            isEditMode
              ? translate('dialogs.cancelEditTrainingQuestion')
              : translate('dialogs.cancelCreateTrainingQuestion')
          }
          cancelButton={translate('common.no')}
          acceptButton={translate('common.yes')}
          onCancel={() => setShowConfirmClose(false)}
          onAccept={() => {
            setShowConfirmClose(false);
            onCloseDialog();
          }}
        />
      )}
    </Dialog>
  );
};
